import React from 'react';
import Data from 'data';
import Locale from 'data/locale';
import Store from 'store';

import Select from 'ui/select';

import './classes.scss';
import { profileSetClass, profileSetFollower, profileSetSeasonal } from 'logic/profileReducers';
import { Seasons } from 'common';
import Tooltip from 'ui/tooltip';
import EditingContext from 'store/editingContext';

const _L = Locale("title");

const seasonThemes = {
  [Seasons.None]: {
    name: "No Season Theme",
  },
  [Seasons.Grandeur]: {
    name: "Grandeur",
    tooltip: "Seasonal characters always have Ring of Royal Grandeur legendary effect.",
  },
  [Seasons.Nightmares]: {
    name: "Nightmares",
    tooltip: "Seasonal characters always have Legacy of Nightmares set bonus.",
  },
  [Seasons.Triune]: {
    name: "Triune",
    tooltip: "Seasonal characters can benefit from Triune circles of power.",
  },
  [Seasons.Pandemonium]: {
    name: "Eternal Conflict",
    tooltip: "Seasonal characters get a stacking Pandemonium buff as they kill enemies.",
  },
  [Seasons.Archives]: {
    name: "Forbidden Archives",
    tooltip: "Seasonal characters can equip any legendary power in any Kanai cube slot.",
  },
  [Seasons.Shadows]: {
    name: "Shadows of the Past",
    tooltip: "Seasonal characters gain a fourth Kanai cube slot that can store any item power.",
  },
  [Seasons.Ethereal]: {
    name: "Ethereals",
    tooltip: "Seasonal characters can obtain powerful Ethereal weapons.",
  },
  [Seasons.SoulShards]: {
    name: "Soul Shards",
    tooltip: "Seasonal characters can socket powerful Soul Shards in helmets and weapons.",
  },
  [Seasons.Sanctified]: {
    name: "Angelic Crucibles",
    tooltip: "Seasonal characters can sanctify an equipped item to greatly increase its power.",
  },
  [Seasons.Altar]: {
    name: "Rites of Sanctuary",
    tooltip: "Seasonal characters can progress through Altar of Rites tree to augment their journey.",
  },
  [Seasons.Enmity]: {
    name: "Visions of Enmity",
    tooltip: "Seasonal characters have special paragon distribution rules.",
  },
};

const useGender = () => Store.useProfile(profile => profile.gender);
const useClass = () => Store.useProfile(profile => profile.class);
const useFollower = () => Store.useProfile(profile => profile.follower);
const useSeasonal = () => Store.useProfile(profile => seasonThemes[profile.seasonal] ? profile.seasonal : Seasons.None);

export function ClassOption({ charClass }) {
  const gender = useGender();
  return (
    <span className="d3p-class-option">
      {!!charClass && <span className={`d3p-class-${charClass} d3p-small d3p-class-icon d3p-${gender}`}/>}
      {charClass ? Data.classes[charClass].name : _L`No follower`}
    </span>
  );
}

const renderClass = cls => <ClassOption charClass={cls}/>;

const renderSeason = season => {
  const theme = seasonThemes[season];
  const label = <div>{_L(theme.name)}</div>;
  if (theme.tooltip) {
    return <Tooltip tooltip={theme.tooltip}>{label}</Tooltip>;
  } else {
    return label;
  }
};

export const SeasonSelect = React.memo(function SeasonSelect() {
  const dispatch = Store.useDispatch();

  const season = useSeasonal();
  const setSeason = React.useCallback(season => dispatch.setProfile(profile => profileSetSeasonal(profile, season)), [dispatch]);
  const seasonOptions = React.useMemo(() => Object.keys(seasonThemes).map(id => parseInt(id)), []);

  return (
    <Select options={seasonOptions}
            value={season}
            onChange={setSeason}
            renderValue={renderSeason}
            renderOption={renderSeason}
            className="d3p-select-season"
    />
  );
});

export default React.memo(function ClassSelect() {
  const [mode, setMode] = EditingContext.useState("paperdollMode", "character");

  //const isCC = Session.useSelector(store => (store.userLevel || 0) >= BuildInfoLevel);
  // React.useEffect(() => {
  //   if (mode !== "character" && !isCC) {
  //     setMode("character");
  //   }
  // }, [isCC, mode, setMode]);

  const dispatch = Store.useDispatch();

  const charClass = useClass();
  const setClass = React.useCallback(cls => dispatch.setProfile(profile => profileSetClass(profile, cls)), [dispatch]);

  const follower = useFollower();
  const setFollower = React.useCallback(cls => dispatch.setProfile(profile => profileSetFollower(profile, cls)), [dispatch]);

  const classOptions = React.useMemo(() => Object.keys(Data.classes).filter(cls => !Data.classes[cls].follower), []);
  const followerOptions = React.useMemo(() => Object.keys(Data.classes).filter(cls => Data.classes[cls].follower), []);

  const classSelect = (mode === "character" ? (
    <Select options={classOptions}
            value={charClass}
            onChange={setClass}
            renderValue={renderClass}
            renderOption={renderClass}
            className="d3p-select-class"
    />
  ) : (
    <div className="d3p-inactive-tab" onClick={() => setMode("character")}>
      <div className="d3p-inactive-tab-inner">
        <ClassOption charClass={charClass}/>
      </div>
    </div>
  ));

  let followerSelect = (mode === "follower" ? (
    <Select options={followerOptions}
            value={follower}
            onChange={setFollower}
            renderValue={renderClass}
            renderOption={renderClass}
            className="d3p-select-class"
            placeholder={<ClassOption charClass={null}/>}
            allowEmpty
    />
  ) : (
    <div className="d3p-inactive-tab" onClick={() => setMode("follower")}>
      <div className="d3p-inactive-tab-inner">
        <ClassOption charClass={follower}/>
      </div>
    </div>
  ));

  return (
    <div className="d3p-ClassSelect">
      <div className="d3p-class-tab">
        {classSelect}
      </div>
      <div className="d3p-class-tab">
        {followerSelect}
      </div>
    </div>
  );
});
